// report_builder/ReportView.jsx
// ============================================================
// REPORT VIEW — top-level container
// Wired into Dashboard.jsx as a single view entry.
// Manages which sub-view is active:
//
//   'list'     → ReportList    (default — all saved reports)
//   'new'      → ReportBuilder (blank)
//   'edit'     → ReportBuilder (loaded report)
//   'run'      → ReportRunner  (scope + date + TF picker)
//   'results'  → ReportResults (output of a run)
//
// No props needed — self-contained.
// ============================================================

import React, { useState } from 'react';
import ReportList    from './ReportList';
import ReportBuilder from './ReportBuilder';
import ReportRunner  from './ReportRunner';
import ReportResults from './ReportResults';
import { fetchReport } from './reportApi';

export default function ReportView() {
  const [view,        setView]        = useState('list');  // list|new|edit|run|results
  const [activeReport, setActiveReport] = useState(null);  // report object
  const [runResults,   setRunResults]   = useState(null);  // results from run

  // ── Navigation handlers ───────────────────────────────────

  const goList = () => {
    setView('list');
    setActiveReport(null);
    setRunResults(null);
  };

  const goNew = () => {
    setActiveReport(null);
    setView('new');
  };

  const goEdit = async (report) => {
    // Fetch full definition before opening editor
    try {
      const full = await fetchReport(report.id);
      setActiveReport(full);
    } catch {
      setActiveReport(report);
    }
    setView('edit');
  };

  const goRun = (report) => {
    setActiveReport(report);
    setView('run');
  };

  const onSaved = (savedReport) => {
    // After save — go back to list so user sees the updated card
    setView('list');
    setActiveReport(null);
  };

  const onResults = (results) => {
    setRunResults(results);
    setView('results');
  };

  // ── Render active sub-view ────────────────────────────────

  if (view === 'new') {
    return (
      <ReportBuilder
        editReport={null}
        onSaved={onSaved}
        onCancel={goList}
      />
    );
  }

  if (view === 'edit') {
    return (
      <ReportBuilder
        editReport={activeReport}
        onSaved={onSaved}
        onCancel={goList}
      />
    );
  }

  if (view === 'run') {
    return (
      <ReportRunner
        report={activeReport}
        onResults={onResults}
        onCancel={goList}
      />
    );
  }

  if (view === 'results') {
    return (
      <ReportResults
        results={runResults}
        onRunAgain={() => setView('run')}
        onNewRun={goList}
      />
    );
  }

  // Default — list view
  return (
    <ReportList
      onNew={goNew}
      onEdit={goEdit}
      onRun={goRun}
    />
  );
}